/**
 * Справочники сайта: контакты, аварийные службы, правление.
 * Разметка 1:1 с legacy htdocs/core/templates/admin_contacts.tpl, admin_services.tpl,
 * admin_managers.tpl и admin_managers_edit.tpl — включая ДВЕ формы у контактов и служб
 * (desktop .hidden-xs с table-bordered и мобильную .hidden-sm.hidden-md.hidden-lg),
 * таблицу .ustable у правления и сообщения .bs-callout / .infobox.
 *
 * В legacy это были три раздела (/admin/contacts, /admin/services, /admin/managers);
 * маршруты App.tsx ведут все три (и /admin/directories) сюда — какой раздел показать,
 * определяется по URL, а /admin/directories показывает их подряд.
 *
 * Legacy отправлял одну POST-форму с name-based dispatch (upd_name[id], contacts-up[N],
 * contacts-delete[id], contacts-submit); здесь та же логика разложена на REST-вызовы
 * Handlers\Admin\Content: PUT по каждой изменённой строке + POST на добавление —
 * ровно то, что делал legacy-контроллер за один сабмит.
 */

import { Fragment, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { api, ApiError } from '@/api/client'
import { useLegacyCss } from '@/lib/legacyAssets'
import { useApi } from '@/lib/useApi'

/** Контакт (таблица contacts). */
interface Contact {
  id: number
  order: number
  name: string
  comment: string
  value: string
  type: string
}

/** Аварийная служба (таблица services). */
interface Service {
  id: number
  order: number
  name: string
  comment: string
  phone: string
}

/** Представитель правления (таблица managers). */
interface Manager {
  id: number
  order: number
  position: string
  name: string
  comment: string
  phone: string
  email: string
}

/** legacy-фильтр шаблона: {$x|replace:'<br />':''} — в поля ввода <br /> не попадает. */
function stripBr(value: string | null): string {
  return (value ?? '').replace(/<br \/>/g, '')
}

/** Сообщения bs-callout (legacy admin_contacts.tpl / admin_services.tpl). */
function Callout({ ok, error }: { ok: string[]; error: string[] }) {
  return (
    <>
      {error.length > 0 && (
        <div className="bs-callout bs-callout-danger">
          <h4>Ошибка</h4>
          {error.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
      {ok.length > 0 && (
        <div className="bs-callout bs-callout-info">
          <h4>OK</h4>
          {ok.map((message, index) => (
            <p key={index}>{message}</p>
          ))}
        </div>
      )}
    </>
  )
}

export function AdminDirectoriesPage() {
  useLegacyCss('admin', 'newapproach', 'docs.min')

  const { pathname } = useLocation()
  const section = pathname.endsWith('/services')
    ? 'services'
    : pathname.endsWith('/managers')
      ? 'managers'
      : pathname.endsWith('/contacts')
        ? 'contacts'
        : 'all'

  return (
    <>
      {(section === 'contacts' || section === 'all') && <ContactsSection />}
      {(section === 'services' || section === 'all') && <ServicesSection />}
      {(section === 'managers' || section === 'all') && <ManagersSection />}
    </>
  )
}

// ─── Контакты (legacy admin_contacts.tpl) ────────────────────────────────────

/** Значения полей строки контакта в форме. */
type ContactDraft = Pick<Contact, 'name' | 'comment' | 'value' | 'type'>

const CONTACT_TYPES: Array<[string, string]> = [
  ['phone', 'Телефон'],
  ['address', 'Адрес'],
  ['email', 'Email'],
  ['link', 'Ссылка'],
  ['string', 'Текст'],
]

function ContactsSection() {
  const { data, reload } = useApi<Contact[]>('/api/admin/content/contacts')

  const [rows, setRows] = useState<Record<number, ContactDraft>>({})
  const [add, setAdd] = useState({ order: '', name: '', comment: '', value: '', type: 'phone' })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const contacts = data ?? []

  useEffect(() => {
    const drafts: Record<number, ContactDraft> = {}
    for (const contact of contacts) {
      drafts[contact.id] = {
        name: contact.name ?? '',
        comment: stripBr(contact.comment),
        value: contact.value ?? '',
        type: contact.type ?? '',
      }
    }
    setRows(drafts)
  }, [data])

  const patch = (id: number, field: keyof ContactDraft, value: string): void => {
    setRows((current) => {
      const row = current[id]
      if (row === undefined) return current
      return { ...current, [id]: { ...row, [field]: value } }
    })
  }

  /** legacy contacts-submit: сохраняет все строки и добавляет новую. */
  const submit = async (): Promise<void> => {
    try {
      for (const contact of contacts) {
        const draft = rows[contact.id]
        if (draft === undefined) continue
        await api.put(`/api/admin/content/contacts/${contact.id}`, draft)
      }
      if (add.name !== '' || add.comment !== '' || add.value !== '') {
        await api.post('/api/admin/content/contacts', {
          order: add.order === '' ? 0 : Number.parseInt(add.order, 10),
          name: add.name,
          comment: add.comment,
          value: add.value,
          type: add.type,
        })
        setAdd({ order: '', name: '', comment: '', value: '', type: 'phone' })
      }
      setOk(['Изменения сохранены.'])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy contacts-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/contacts/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  /** legacy contacts-up[N] / contacts-down[N]. */
  const move = async (id: number, dir: 'up' | 'down'): Promise<void> => {
    try {
      const result = await api.post<{ message: string }>(
        `/api/admin/content/contacts/${id}/move`,
        { dir },
      )
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при перемещении.'])
      setOk([])
    }
  }

  const typeOptions = CONTACT_TYPES.map(([value, label]) => (
    <option key={value} value={value}>
      {label}
    </option>
  ))

  return (
    <>
      <Callout ok={ok} error={error} />

      <form
        className="form-contacts hidden-xs"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-bordered table-condensed">
          <thead>
            <tr>
              <th colSpan={2}>⇕</th>
              <th>№</th>
              <th>Название</th>
              <th>Комментарий</th>
              <th>Значение</th>
              <th>Тип</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {contacts.map((contact, index) => (
              <tr key={contact.id}>
                <td>
                  {index !== 0 && (
                    <input
                      type="submit"
                      name={`contacts-up[${index + 1}]`}
                      className="btn btn-xs btn-warning"
                      value="⇑"
                      onClick={(e) => {
                        e.preventDefault()
                        void move(contact.id, 'up')
                      }}
                    />
                  )}
                </td>
                <td>
                  {index !== contacts.length - 1 && (
                    <input
                      type="submit"
                      name={`contacts-down[${index + 1}]`}
                      className="btn btn-xs btn-warning"
                      value="⇓"
                      onClick={(e) => {
                        e.preventDefault()
                        void move(contact.id, 'down')
                      }}
                    />
                  )}
                </td>
                <td>{index + 1}</td>
                <td>
                  <input
                    className="form-control"
                    name={`upd_name[${contact.id}]`}
                    value={rows[contact.id]?.name ?? ''}
                    onChange={(e) => patch(contact.id, 'name', e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                    className="form-control"
                    name={`upd_comment[${contact.id}]`}
                    value={rows[contact.id]?.comment ?? ''}
                    onChange={(e) => patch(contact.id, 'comment', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    className="form-control"
                    name={`upd_value[${contact.id}]`}
                    value={rows[contact.id]?.value ?? ''}
                    onChange={(e) => patch(contact.id, 'value', e.target.value)}
                  />
                </td>
                <td>
                  <select
                    className="form-control"
                    name={`upd_type[${contact.id}]`}
                    value={rows[contact.id]?.type ?? ''}
                    onChange={(e) => patch(contact.id, 'type', e.target.value)}
                  >
                    {typeOptions}
                  </select>
                </td>
                <td>
                  <input
                    type="submit"
                    name={`contacts-delete[${contact.id}]`}
                    className="btn btn-xs btn-danger"
                    value="удалить"
                    onClick={(e) => {
                      e.preventDefault()
                      void remove(contact.id)
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={3}>
                Перед №{' '}
                <input
                  className="form-control form-inline-num"
                  name="add_order"
                  value={add.order}
                  onChange={(e) => setAdd({ ...add, order: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="add_name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
              <td>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_comment"
                  value={add.comment}
                  onChange={(e) => setAdd({ ...add, comment: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="add_value"
                  value={add.value}
                  onChange={(e) => setAdd({ ...add, value: e.target.value })}
                />
              </td>
              <td>
                <select
                  className="form-control"
                  name="add_type"
                  value={add.type}
                  onChange={(e) => setAdd({ ...add, type: e.target.value })}
                >
                  {typeOptions}
                </select>
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="contacts-submit"
          className="btn btn-xs btn-info"
          value="сохранить изменения"
        />
      </form>

      <form
        className="form-contacts hidden-sm hidden-md hidden-lg"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-condensed">
          <tbody>
            {contacts.map((contact, index) => (
              <Fragment key={contact.id}>
                <tr>
                  <td style={{ paddingTop: '10px' }}>
                    {index !== 0 && (
                      <input
                        type="submit"
                        name={`contacts-up[${index + 1}]`}
                        className="btn btn-xs btn-warning"
                        value="⇑"
                        style={{ marginRight: '10px' }}
                        onClick={(e) => {
                          e.preventDefault()
                          void move(contact.id, 'up')
                        }}
                      />
                    )}
                    {index !== contacts.length - 1 && (
                      <input
                        type="submit"
                        name={`contacts-down[${index + 1}]`}
                        className="btn btn-xs btn-warning"
                        value="⇓"
                        onClick={(e) => {
                          e.preventDefault()
                          void move(contact.id, 'down')
                        }}
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>№: {index + 1}</td>
                </tr>
                <tr>
                  <td>
                    <label>Название</label>
                    <input
                      className="form-control"
                      name={`upd_name[${contact.id}]`}
                      value={rows[contact.id]?.name ?? ''}
                      onChange={(e) => patch(contact.id, 'name', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>Комментарий</label>
                    <textarea
                      style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                      className="form-control"
                      name={`upd_comment[${contact.id}]`}
                      value={rows[contact.id]?.comment ?? ''}
                      onChange={(e) => patch(contact.id, 'comment', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>Значение</label>
                    <input
                      className="form-control"
                      name={`upd_value[${contact.id}]`}
                      value={rows[contact.id]?.value ?? ''}
                      onChange={(e) => patch(contact.id, 'value', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>Тип</label>
                    <select
                      className="form-control"
                      name={`upd_type[${contact.id}]`}
                      value={rows[contact.id]?.type ?? ''}
                      onChange={(e) => patch(contact.id, 'type', e.target.value)}
                    >
                      {typeOptions}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      borderBottom: '3px solid grey',
                    }}
                  >
                    <input
                      type="submit"
                      name={`contacts-delete[${contact.id}]`}
                      className="btn btn-xs btn-danger"
                      value="Удалить"
                      onClick={(e) => {
                        e.preventDefault()
                        void remove(contact.id)
                      }}
                    />
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
        <table className="table table-hover table-condensed">
          <tbody>
            <tr>
              <td colSpan={3}>
                Перед №{' '}
                <input
                  className="form-control form-inline-num"
                  name="add_order"
                  value={add.order}
                  onChange={(e) => setAdd({ ...add, order: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Название</label>
                <input
                  className="form-control"
                  name="add_name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Комментарий</label>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_comment"
                  value={add.comment}
                  onChange={(e) => setAdd({ ...add, comment: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Значение</label>
                <input
                  className="form-control"
                  name="add_value"
                  value={add.value}
                  onChange={(e) => setAdd({ ...add, value: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Тип</label>
                <select
                  className="form-control"
                  name="add_type"
                  value={add.type}
                  onChange={(e) => setAdd({ ...add, type: e.target.value })}
                >
                  {typeOptions}
                </select>
              </td>
            </tr>
            <tr>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="contacts-submit"
          className="btn btn-xs btn-info"
          value="Сохранить изменения"
        />
      </form>
    </>
  )
}

// ─── Аварийные службы (legacy admin_services.tpl) ────────────────────────────

type ServiceDraft = Pick<Service, 'name' | 'comment' | 'phone'>

function ServicesSection() {
  const { data, reload } = useApi<Service[]>('/api/admin/content/services')

  const [rows, setRows] = useState<Record<number, ServiceDraft>>({})
  const [add, setAdd] = useState({ order: '', name: '', comment: '', phone: '' })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const services = data ?? []

  useEffect(() => {
    const drafts: Record<number, ServiceDraft> = {}
    for (const service of services) {
      drafts[service.id] = {
        name: service.name ?? '',
        comment: stripBr(service.comment),
        phone: stripBr(service.phone),
      }
    }
    setRows(drafts)
  }, [data])

  const patch = (id: number, field: keyof ServiceDraft, value: string): void => {
    setRows((current) => {
      const row = current[id]
      if (row === undefined) return current
      return { ...current, [id]: { ...row, [field]: value } }
    })
  }

  /** legacy services-submit. */
  const submit = async (): Promise<void> => {
    try {
      for (const service of services) {
        const draft = rows[service.id]
        if (draft === undefined) continue
        await api.put(`/api/admin/content/services/${service.id}`, draft)
      }
      if (add.name !== '' || add.comment !== '' || add.phone !== '') {
        await api.post('/api/admin/content/services', {
          order: add.order === '' ? 0 : Number.parseInt(add.order, 10),
          name: add.name,
          comment: add.comment,
          phone: add.phone,
        })
        setAdd({ order: '', name: '', comment: '', phone: '' })
      }
      setOk(['Изменения сохранены.'])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy services-delete[id]. */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/services/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  /** legacy services-up[N] / services-down[N]. */
  const move = async (id: number, dir: 'up' | 'down'): Promise<void> => {
    try {
      const result = await api.post<{ message: string }>(
        `/api/admin/content/services/${id}/move`,
        { dir },
      )
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при перемещении.'])
      setOk([])
    }
  }

  return (
    <>
      <Callout ok={ok} error={error} />

      <form
        className="form-services hidden-xs"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-bordered table-condensed">
          <thead>
            <tr>
              <th colSpan={2}>⇕</th>
              <th>№</th>
              <th>Название</th>
              <th>Комментарий</th>
              <th>Телефон</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {services.map((service, index) => (
              <tr key={service.id}>
                <td>
                  {index !== 0 && (
                    <input
                      type="submit"
                      name={`services-up[${index + 1}]`}
                      className="btn btn-xs btn-warning"
                      value="⇑"
                      onClick={(e) => {
                        e.preventDefault()
                        void move(service.id, 'up')
                      }}
                    />
                  )}
                </td>
                <td>
                  {index !== services.length - 1 && (
                    <input
                      type="submit"
                      name={`services-down[${index + 1}]`}
                      className="btn btn-xs btn-warning"
                      value="⇓"
                      onClick={(e) => {
                        e.preventDefault()
                        void move(service.id, 'down')
                      }}
                    />
                  )}
                </td>
                <td>{index + 1}</td>
                <td>
                  <input
                    className="form-control"
                    name={`upd_name[${service.id}]`}
                    value={rows[service.id]?.name ?? ''}
                    onChange={(e) => patch(service.id, 'name', e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    className="form-control"
                    name={`upd_comment[${service.id}]`}
                    style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                    value={rows[service.id]?.comment ?? ''}
                    onChange={(e) => patch(service.id, 'comment', e.target.value)}
                  />
                </td>
                <td>
                  <textarea
                    style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                    className="form-control"
                    name={`upd_phone[${service.id}]`}
                    value={rows[service.id]?.phone ?? ''}
                    onChange={(e) => patch(service.id, 'phone', e.target.value)}
                  />
                </td>
                <td>
                  <input
                    type="submit"
                    name={`services-delete[${service.id}]`}
                    className="btn btn-xs btn-danger"
                    value="удалить"
                    onClick={(e) => {
                      e.preventDefault()
                      void remove(service.id)
                    }}
                  />
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={3}>
                Перед №{' '}
                <input
                  className="form-control form-inline-num"
                  name="add_order"
                  value={add.order}
                  onChange={(e) => setAdd({ ...add, order: e.target.value })}
                />
              </td>
              <td>
                <input
                  className="form-control"
                  name="add_name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
              <td>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_comment"
                  value={add.comment}
                  onChange={(e) => setAdd({ ...add, comment: e.target.value })}
                />
              </td>
              <td>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_phone"
                  value={add.phone}
                  onChange={(e) => setAdd({ ...add, phone: e.target.value })}
                />
              </td>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="services-submit"
          className="btn btn-xs btn-info"
          value="сохранить изменения"
        />
      </form>

      <form
        className="form-services hidden-sm hidden-md hidden-lg"
        method="POST"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        <table className="table table-hover table-condensed">
          <tbody>
            {services.map((service, index) => (
              <Fragment key={service.id}>
                <tr>
                  <td style={{ paddingTop: '10px' }}>
                    {index !== 0 && (
                      <input
                        type="submit"
                        name={`services-up[${index + 1}]`}
                        className="btn btn-xs btn-warning"
                        value="⇑"
                        style={{ marginRight: '10px' }}
                        onClick={(e) => {
                          e.preventDefault()
                          void move(service.id, 'up')
                        }}
                      />
                    )}
                    {index !== services.length - 1 && (
                      <input
                        type="submit"
                        name={`services-down[${index + 1}]`}
                        className="btn btn-xs btn-warning"
                        value="⇓"
                        onClick={(e) => {
                          e.preventDefault()
                          void move(service.id, 'down')
                        }}
                      />
                    )}
                  </td>
                </tr>
                <tr>
                  <td>№: {index + 1}</td>
                </tr>
                <tr>
                  <td>
                    <label>Название</label>
                    <input
                      className="form-control"
                      name={`upd_name[${service.id}]`}
                      value={rows[service.id]?.name ?? ''}
                      onChange={(e) => patch(service.id, 'name', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>Комментарий</label>
                    <textarea
                      className="form-control"
                      name={`upd_comment[${service.id}]`}
                      style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                      value={rows[service.id]?.comment ?? ''}
                      onChange={(e) => patch(service.id, 'comment', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label>Телефон</label>
                    <textarea
                      style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                      className="form-control"
                      name={`upd_phone[${service.id}]`}
                      value={rows[service.id]?.phone ?? ''}
                      onChange={(e) => patch(service.id, 'phone', e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td
                    style={{
                      paddingTop: '10px',
                      paddingBottom: '10px',
                      borderBottom: '3px solid grey',
                    }}
                  >
                    <input
                      type="submit"
                      name={`services-delete[${service.id}]`}
                      className="btn btn-xs btn-danger"
                      value="Удалить"
                      onClick={(e) => {
                        e.preventDefault()
                        void remove(service.id)
                      }}
                    />
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
        <table className="table table-hover table-condensed">
          <tbody>
            <tr>
              <td colSpan={3}>
                Перед №{' '}
                <input
                  className="form-control form-inline-num"
                  name="add_order"
                  value={add.order}
                  onChange={(e) => setAdd({ ...add, order: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Название</label>
                <input
                  className="form-control"
                  name="add_name"
                  value={add.name}
                  onChange={(e) => setAdd({ ...add, name: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Комментарий</label>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_comment"
                  value={add.comment}
                  onChange={(e) => setAdd({ ...add, comment: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Телефон</label>
                <textarea
                  style={{ height: '62px', resize: 'none', padding: '0 6px' }}
                  className="form-control"
                  name="add_phone"
                  value={add.phone}
                  onChange={(e) => setAdd({ ...add, phone: e.target.value })}
                />
              </td>
            </tr>
            <tr>
              <td />
            </tr>
          </tbody>
        </table>
        <input
          type="submit"
          name="services-submit"
          className="btn btn-xs btn-info"
          value="Cохранить изменения"
        />
      </form>
    </>
  )
}

// ─── Правление (legacy admin_managers.tpl + admin_managers_edit.tpl) ─────────

function ManagersSection() {
  const { data, reload } = useApi<Manager[]>('/api/admin/content/managers')

  /** null — список; 0 — новый представитель; иначе id (legacy /admin/managers/{id}). */
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState({
    position: '',
    name: '',
    email: '',
    comment: '',
    phone: '',
  })
  const [ok, setOk] = useState<string[]>([])
  const [error, setError] = useState<string[]>([])

  const { data: manager } = useApi<Manager>(
    editId !== null && editId > 0 ? `/api/admin/content/managers/${editId}` : null,
  )

  useEffect(() => {
    if (manager === null) return
    setForm({
      position: manager.position ?? '',
      name: manager.name ?? '',
      email: manager.email ?? '',
      comment: stripBr(manager.comment),
      phone: stripBr(manager.phone),
    })
  }, [manager])

  const managers = data ?? []

  const openEditor = (id: number): void => {
    setEditId(id)
    setForm({ position: '', name: '', email: '', comment: '', phone: '' })
    setOk([])
    setError([])
  }

  /** legacy managers_submit. */
  const submit = async (): Promise<void> => {
    if (editId === null) return
    try {
      const result =
        editId > 0
          ? await api.put<{ message: string }>(`/api/admin/content/managers/${editId}`, form)
          : await api.post<{ id: number; message: string }>('/api/admin/content/managers', form)
      setOk([result.message])
      setError([])
      reload()
      if (editId === 0) setEditId(null)
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при сохранении.'])
      setOk([])
    }
  }

  /** legacy managers-delete[id] (мягкое удаление is_delete). */
  const remove = async (id: number): Promise<void> => {
    try {
      const result = await api.delete<{ message: string }>(`/api/admin/content/managers/${id}`)
      setOk([result.message])
      setError([])
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при удалении.'])
      setOk([])
    }
  }

  /** legacy managers-up[N] (кнопка «вниз» в шаблоне закомментирована). */
  const moveUp = async (id: number): Promise<void> => {
    try {
      await api.post(`/api/admin/content/managers/${id}/move`, { dir: 'up' })
      reload()
    } catch (e: unknown) {
      setError([e instanceof ApiError ? e.message : 'Ошибка при перемещении.'])
    }
  }

  const infobox = (
    <>
      {error.map((message, index) => (
        <p key={index} className="okke" style={{ color: '#ce4844', float: 'left' }}>
          {message}
        </p>
      ))}
      {ok.map((message, index) => (
        <p key={index} className="okke" style={{ float: 'left' }}>
          {message}
        </p>
      ))}
    </>
  )

  // ── Редактор представителя (legacy admin_managers_edit.tpl) ──────────────
  if (editId !== null) {
    return (
      <>
        <div className="infobox clearfix">
          {infobox}
          <div style={{ float: 'right' }}>
            <a
              className="bt"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setEditId(null)
                setOk([])
                setError([])
              }}
            >
              К списку представителей
            </a>
          </div>
        </div>

        <form
          className="add-form"
          method="POST"
          onSubmit={(e) => {
            e.preventDefault()
            void submit()
          }}
        >
          <div className="form-input">
            <span>Должность:&nbsp;</span>
            <input
              name="manager_position"
              value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
          </div>

          <div className="form-input">
            <span>Имя:&nbsp;</span>
            <input
              name="manager_name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>

          <div className="form-input">
            <span>E-Mail:&nbsp;</span>
            <input
              name="manager_email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>

          <div className="form-input">
            <span>Коментарий:&nbsp;</span>
            <textarea
              rows={5}
              name="manager_comment"
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
            />
          </div>

          <div className="form-input">
            <span>Телефоны:&nbsp;</span>
            <textarea
              rows={5}
              name="manager_phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>

          <div style={{ textAlign: 'center' }}>
            <input
              type="submit"
              name="managers_submit"
              className={`bt ${editId > 0 ? 'orange' : 'green'}`}
              value={editId > 0 ? 'Сохранить изменения' : 'Добавить представителя'}
            />
          </div>
        </form>
      </>
    )
  }

  // ── Список правления (legacy admin_managers.tpl) ─────────────────────────
  return (
    <>
      <div className="infobox clearfix">
        {infobox}
        <div style={{ float: 'right' }}>
          <a
            className="bt green"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              openEditor(0)
            }}
          >
            добавить представителя
          </a>
        </div>
      </div>

      <form
        className="form-managers form-news"
        method="POST"
        onSubmit={(e) => e.preventDefault()}
      >
        <table className="ustable">
          <tbody>
            {managers.map((item, index) => (
              <tr key={item.id}>
                <td width={50}>
                  {index !== 0 && (
                    <input
                      type="submit"
                      name={`managers-up[${index + 1}]`}
                      className="bt orange"
                      value="⇑"
                      onClick={() => void moveUp(item.id)}
                    />
                  )}
                </td>
                <td>
                  <span style={{ fontWeight: 800 }}>{item.position}</span>
                  <br />
                  {item.name}
                </td>
                <td dangerouslySetInnerHTML={{ __html: item.comment ?? '' }} />
                <td dangerouslySetInnerHTML={{ __html: item.phone ?? '' }} />
                <td>{item.email}</td>
                <td width={210} style={{ textAlign: 'right', paddingRight: '10px' }}>
                  <a
                    className="bt orange"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      openEditor(item.id)
                    }}
                  >
                    редактировать
                  </a>
                  <input
                    type="submit"
                    name={`managers-delete[${item.id}]`}
                    className="bt red"
                    value="удалить"
                    onClick={() => void remove(item.id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </form>
    </>
  )
}
